import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { AssetBundle, Mutator } from "../../../models/types";
import { MutatorOptions } from "./MutatorOptions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { fetchHtmlFromS3 } from "../../../common/MutatorHelpers";
import SeepiaLogo from "../../../assets/Seepia-white.png";
import SeepiaFavicon from "../../../assets/Seepia-favicon.png";
import PhoneMockup from "../../../iPhone-X-Mockup_landscape.png";
import cx from "classnames";
import styles from "./MutatorView.module.scss";

//const DEMO_BASE_URL =
//    "https://customator-demo.s3.eu-central-1.amazonaws.com/customator";
// Use this url for the actual demo page
const DEMO_BASE_URL = "https://d29rwakebcagi9.cloudfront.net/customator";
const CONFIG_URL = `${DEMO_BASE_URL}/mutator-config.json`;
const BUILD_ROOT_URL = `${DEMO_BASE_URL}/index.html`;

/**
 * DEMO: Walks the mutator config and collects all unique assetbundle paths,
 * then builds AssetBundle[] with full URLs (replaces MUTATOR_ASSETBUNDLES query).
 */
const deriveAssetBundles = (
    config: Mutator,
    demoBaseUrl: string
): AssetBundle[] => {
    const paths = new Set<string>();

    const collectFromProperties = (
        properties: { type: string; value?: any }[]
    ) => {
        for (const prop of properties) {
            if (prop.type === "assetbundle" && typeof prop.value === "string") {
                paths.add(prop.value);
            }
        }
    };

    // Top-level properties
    collectFromProperties(config.properties);

    // Type definitions (e.g. $hero has "Asset bundle" property)
    for (const typeDef of Object.values(config.types)) {
        // Type properties don't have values themselves, but enum options do
        for (const prop of typeDef.properties) {
            if (prop.type === "assetbundle") {
                // Scan all enum options that use types containing this assetbundle property
                for (const enumDef of Object.values(config.enums)) {
                    if (
                        enumDef.type ===
                        Object.keys(config.types).find(
                            k => config.types[k] === typeDef
                        )
                    ) {
                        for (const option of enumDef.options) {
                            const val = option.value as Record<string, unknown>;
                            if (val && typeof val[prop.name] === "string") {
                                paths.add(val[prop.name] as string);
                            }
                        }
                    }
                }
            }
        }
    }

    return Array.from(paths).map(path => ({
        name: path.split("/").pop() || path,
        path,
        url: `${demoBaseUrl}/${path}.js`
    }));
};

const MutatorViewImplementation = () => {
    const [mutatorWorkingCopy, setMutatorWorkingCopy] = useState<
        Mutator | undefined
    >(undefined);
    const [assetBundles, setAssetBundles] = useState<AssetBundle[]>([]);
    const [portrait, setPortrait] = useState(window.innerWidth > 500);
    const [showMeta, setShowMeta] = useState(false);
    const [iFrameContent, setIFrameContent] = useState<string | undefined>(
        undefined
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | undefined>(undefined);
    const [showBuildModal, setShowBuildModal] = useState(false);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(CONFIG_URL);
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch config: ${response.status}`
                    );
                }
                const config: Mutator = await response.json();
                const bundles = deriveAssetBundles(config, DEMO_BASE_URL);
                setMutatorWorkingCopy(config);
                setAssetBundles(bundles);
            } catch (err) {
                console.error("[MutatorView] Failed to load config:", err);
                setError(
                    err instanceof Error ? err.message : "Failed to load config"
                );
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const updateIframe = useCallback(async () => {
        if (!mutatorWorkingCopy || assetBundles.length === 0) return;
        const html = await fetchHtmlFromS3(
            BUILD_ROOT_URL,
            mutatorWorkingCopy,
            assetBundles
        );
        setIFrameContent(html);
    }, [mutatorWorkingCopy, assetBundles]);

    useEffect(() => {
        updateIframe();
    }, [updateIframe]);

    const onChange = (name: string, value: any) => {
        if (!mutatorWorkingCopy) {
            return;
        }
        const newMutator: Mutator = {
            ...mutatorWorkingCopy,
            properties: mutatorWorkingCopy.properties.map(property => {
                if (property.name === name) {
                    return { ...property, value };
                } else {
                    return { ...property };
                }
            })
        };
        setMutatorWorkingCopy(newMutator);
        // Re-derive asset bundles in case an assetbundle property changed
        setAssetBundles(deriveAssetBundles(newMutator, DEMO_BASE_URL));
    };

    if (loading && !mutatorWorkingCopy) {
        return (
            <div
                className="text-inverse d-flex justify-content-center align-items-center"
                style={{ minHeight: 300 }}
            >
                <div>
                    <h5 className="text-center">Loading...</h5>
                </div>
            </div>
        );
    }

    if (error || !mutatorWorkingCopy) {
        return (
            <div
                className="text-inverse d-flex justify-content-center align-items-center"
                style={{ minHeight: 300 }}
            >
                <div>
                    <h5 className="text-center">
                        Failed to load configuration
                    </h5>
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mutatorLayout}>
            <div className={cx(styles.glowOrb, styles.glowPurple)} />
            <div className={cx(styles.glowOrb, styles.glowOrange)} />
            <div className={styles.mutatorContent}>
                <div className={styles.mutatorHeader}>
                    <div className={styles.headerBrand}>
                        <a
                            href="https://seepia.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={SeepiaLogo}
                                alt="Seepia Logo"
                                className={styles.seepiaLogo}
                            />
                        </a>
                        <span className={styles.heroTagline}>
                            Customator - <i>Demo version</i>
                        </span>
                    </div>
                    <div className={styles.headerActions}>
                        <Button
                            className={styles.buildButton}
                            onClick={() => setShowBuildModal(true)}
                        >
                            Build
                        </Button>
                        <a
                            href="https://seepia.com/#contact"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.contactLink}
                        >
                            Contact
                        </a>
                    </div>
                </div>

                <div
                    className={cx(styles.mutatorView, {
                        [styles.portraitLayout]: portrait,
                        [styles.landscapeLayout]: !portrait
                    })}
                >
                    <div className={cx(styles.mutatorPreview, "pe-0.5")}>
                        <Form.Group
                            className={cx(
                                "d-flex justify-content-center gap-3",
                                styles.orientationToggle
                            )}
                        >
                            <Form.Label
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => setPortrait(true)}
                                className={
                                    portrait
                                        ? "fw-bold text-primary mb-0"
                                        : "text-inverse-75 mb-0"
                                }
                            >
                                Portrait
                            </Form.Label>

                            <Form.Label
                                role="button"
                                style={{ cursor: "pointer" }}
                                onClick={() => setPortrait(false)}
                                className={
                                    !portrait
                                        ? "fw-bold text-primary mb-0"
                                        : "text-inverse-75 mb-0"
                                }
                            >
                                Landscape
                            </Form.Label>
                        </Form.Group>
                        <div
                            className={cx(styles.phoneFrame, {
                                [styles.phoneFramePortrait]: portrait,
                                [styles.phoneFrameLandscape]: !portrait
                            })}
                        >
                            <img
                                src={PhoneMockup}
                                alt=""
                                className={styles.phoneMockup}
                            />
                            <iframe
                                className={styles.phoneIframe}
                                title="Customator"
                                srcDoc={iFrameContent}
                            />
                        </div>
                    </div>

                    <div className={styles.mutatorOptions}>
                        <Row>
                            <Col
                                xs={"auto"}
                                className={cx(
                                    "d-flex align-items-center justify-content-start me-auto",
                                    styles.instructionText
                                )}
                            >
                                Try out different options and values!{" "}
                            </Col>
                            <Col
                                xs={"auto"}
                                className={styles.showDescriptions}
                            >
                                <div
                                    onClick={() => setShowMeta(!showMeta)}
                                    className={cx(
                                        styles.infoIcon,
                                        "text-inverse-75"
                                    )}
                                >
                                    <span
                                        className={
                                            "mx-2 text-inverse-50 bread-text"
                                        }
                                    >
                                        {!showMeta
                                            ? "Show descriptions"
                                            : "Hide descriptions"}
                                    </span>
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </div>
                            </Col>
                        </Row>
                        <MutatorOptions
                            mutatorConfig={mutatorWorkingCopy}
                            assetBundles={assetBundles}
                            showMetaGlobal={showMeta}
                            onChange={onChange}
                        />
                        <Row className={styles.mobileOnly}>
                            <Col className="col-auto mt-2">
                                <Button
                                    className={styles.buildButton}
                                    onClick={() => setShowBuildModal(true)}
                                >
                                    Build
                                </Button>
                            </Col>
                            <Col className="col-auto mt-2">
                                <a
                                    href="https://seepia.com/#contact"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.contactLink}
                                >
                                    Contact
                                </a>
                            </Col>
                            <div className={`${styles.footerMobile} mt-5`}>
                                © 2026 Seepia. All rights reserved.
                            </div>
                        </Row>
                    </div>
                </div>
                <Row className={styles.footer}>
                    © 2026 Seepia. All rights reserved.
                </Row>
            </div>
            <Modal
                show={showBuildModal}
                onHide={() => setShowBuildModal(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton className="border-0 mt-3">
                    <Modal.Title
                        className={cx(styles.modalTitle, "w-100 text-center")}
                    >
                        Get ready to build your new ads!
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column flex-sm-row mx-1 mx-sm-4 py-3 py-sm-4 align-items-center gap-3">
                    <p className="" style={{ fontSize: "1.1rem" }}>
                        This is a demo version of the Seepia Customator.{" "}
                        <br style={{ marginBottom: "0.8rem" }} />
                        In the full version, you would be able to start building
                        the new customized version of your Seepia playable ad,
                        see the build progress and download the ready-to-use ads
                        from the Customator environment in real time.
                    </p>
                    <p className="ms-sm-3 flex-shrink-0 mb-0">
                        {" "}
                        <img
                            src={SeepiaFavicon}
                            alt="Seepia Logo"
                            className={styles.modalFavicon}
                            style={{
                                width: "clamp(80px, 12vw, 160px)",
                                height: "auto"
                            }}
                        />
                    </p>
                </Modal.Body>
                <Modal.Footer className="border-0 mb-3 mb-sm-5 mt-2 d-flex justify-content-center">
                    <a
                        href="https://seepia.com/#contact"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cx(
                            styles.contactLink,
                            styles.modalContactLink
                        )}
                    >
                        Contact our sales team
                    </a>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export const MutatorView = React.memo(MutatorViewImplementation);
